import { z } from 'zod';
import { riskLevelSchema } from './incident.js';
/**
 * Outbound alerting — channels, categories and the policy vocabulary.
 *
 * Companion to `docs/architecture/outbound-alerting-design.md`, which records
 * why each of these exists. The decision numbers below (D7, D25, …) refer to it.
 *
 * Three registries, declared here rather than stored in tables for the reason
 * `FEATURES` established: the failure mode is not somebody making a wrong
 * choice, it is somebody never being ASKED to make one. A channel added without
 * a transport, or a category added without a disposition, fails the build rather
 * than shipping as whatever the default happened to be.
 *
 * Severity is `riskLevelSchema`, reused deliberately — incidents are the primary
 * alert source and they carry `riskLevel`, so a floor expressed in any other
 * vocabulary could not be compared against the thing it is filtering.
 */
// ── channels ────────────────────────────────────────────────────────────────
export const CHANNEL_KEYS = ['EMAIL', 'TELEGRAM', 'SMS', 'WHATSAPP'];
export const channelKeySchema = z.enum(CHANNEL_KEYS);
/**
 * How a destination is addressed. **The extensibility decision** (D7).
 *
 * Email, SMS, WhatsApp and Telegram address a PERSON. Slack, Zoho Cliq and
 * Teams address a WORKSPACE destination — a channel in a workspace the TENANT
 * installed, which no individual owns. Every launch channel is PERSON; the
 * field exists from the first commit because a model that assumes
 * person-addressing needs a migration and a fan-out rewrite to take the others.
 */
export const channelAddressingSchema = z.enum(['PERSON', 'WORKSPACE']);
/**
 * Whether a channel is really wired up.
 *
 * Phase 1 ships EMAIL and TELEGRAM; SMS and WHATSAPP are declared because their
 * commercial and legal shape is already decided, and they wait on A2P
 * registration and Meta verification rather than on design. Saying so here beats
 * a comment: a registry test asserts every AVAILABLE channel has a transport,
 * and the UI will not offer a PLANNED one. "Not implemented" and "not
 * applicable" are different states and this names which.
 */
export const channelStatusSchema = z.enum(['AVAILABLE', 'PLANNED']);
export const CHANNELS = {
    EMAIL: {
        label: 'Email',
        description: 'Full detail, attachments and tenant branding.',
        addressing: 'PERSON',
        status: 'AVAILABLE',
        // Without a confirm-link, bounces and complaints destroy the sending
        // domain's reputation for every tenant at once.
        requiresVerification: true,
        requiresApprovedTemplate: false,
        capabilities: {
            attachments: true,
            buttons: false,
            images: true,
            richText: 'HTML',
            deliveryReceipts: true,
            maxLength: null,
        },
        costClass: 'FREE',
    },
    TELEGRAM: {
        label: 'Telegram',
        description: 'Immediate chat delivery. Requires a one-time bot handshake.',
        addressing: 'PERSON',
        status: 'AVAILABLE',
        // A bot CANNOT message a user who has not messaged it first — the chat id
        // does not exist until they do. Easiest to send, hardest to enroll.
        requiresVerification: true,
        requiresApprovedTemplate: false,
        capabilities: {
            attachments: true,
            buttons: true,
            images: true,
            richText: 'MARKDOWN',
            deliveryReceipts: false,
            maxLength: 4096,
        },
        costClass: 'FREE',
    },
    SMS: {
        label: 'SMS',
        description: 'Short critical notice with a link. Costs per message.',
        addressing: 'PERSON',
        status: 'PLANNED',
        // STOP/opt-out handling is legally required in many jurisdictions.
        requiresVerification: true,
        requiresApprovedTemplate: false,
        capabilities: {
            attachments: false,
            buttons: false,
            images: false,
            richText: 'NONE',
            deliveryReceipts: true,
            // The renderer enforces this rather than letting the transport truncate
            // mid-sentence. One segment; detail goes behind a link.
            maxLength: 160,
        },
        costClass: 'METERED',
    },
    WHATSAPP: {
        label: 'WhatsApp',
        description: 'Approved templates only. Costs per conversation.',
        addressing: 'PERSON',
        status: 'PLANNED',
        requiresVerification: true,
        // Business-initiated messages must use a pre-approved template; free-form is
        // permitted only inside a 24-hour window the customer opens, which never
        // applies to alerting.
        requiresApprovedTemplate: true,
        capabilities: {
            attachments: true,
            buttons: true,
            images: true,
            richText: 'NONE',
            deliveryReceipts: true,
            maxLength: 1024,
        },
        costClass: 'METERED',
    },
};
export const AVAILABLE_CHANNEL_KEYS = CHANNEL_KEYS.filter((k) => CHANNELS[k].status === 'AVAILABLE');
// ── categories and dispositions ─────────────────────────────────────────────
/**
 * What a person may do about a category (D25, §4).
 *
 * `UNAVAILABLE` is a disposition rather than an absence on purpose, so a screen
 * can show a category greyed with a reason instead of hiding it — the same
 * argument that makes the platform feature list registry-driven.
 */
export const dispositionSchema = z.enum(['MANDATORY', 'DEFAULT_ON', 'OPT_IN', 'UNAVAILABLE']);
export const ALERT_CATEGORY_KEYS = [
    'SECURITY_ALERT',
    'ACCOUNT_ACTIVITY',
    'PLATFORM_SERVICE_NOTICE',
    'TENANT_ANNOUNCEMENT',
    'SCHEDULED_REPORT',
    'VEHICLE_SECURITY',
    'OPERATIONAL_INCIDENT',
    'TELEMETRY_FAULT',
    'MAINTENANCE_RECOMMENDATION',
];
export const alertCategorySchema = z.enum(ALERT_CATEGORY_KEYS);
/** Where an event in this category comes from. Broadcast is constrained (D31). */
export const categoryOriginSchema = z.enum(['SYSTEM', 'BROADCAST']);
export const ALERT_CATEGORIES = {
    SECURITY_ALERT: {
        label: 'Security alerts',
        description: 'Suspected compromise, credential misuse, session revocation.',
        origin: 'SYSTEM',
        defaultDisposition: 'MANDATORY',
        tenantMandatable: false,
        platformOnly: true,
        floor: 'LOW',
    },
    ACCOUNT_ACTIVITY: {
        label: 'Account activity',
        description: 'Sign-in from a new device, password and access changes.',
        origin: 'SYSTEM',
        defaultDisposition: 'MANDATORY',
        tenantMandatable: false,
        platformOnly: true,
        floor: 'LOW',
    },
    PLATFORM_SERVICE_NOTICE: {
        label: 'Service notices',
        description: 'Planned maintenance, incidents and required service changes.',
        origin: 'BROADCAST',
        defaultDisposition: 'MANDATORY',
        tenantMandatable: false,
        platformOnly: true,
        floor: 'LOW',
    },
    TENANT_ANNOUNCEMENT: {
        label: 'Announcements from your organisation',
        description: 'Free-text messages sent by your own administrators.',
        origin: 'BROADCAST',
        // DEFAULT_ON and NOT raisable — see `tenantMandatable` above (D31).
        defaultDisposition: 'DEFAULT_ON',
        tenantMandatable: false,
        platformOnly: false,
        floor: 'LOW',
    },
    SCHEDULED_REPORT: {
        label: 'Scheduled reports',
        description: 'The recurring summaries your administrators set up for you.',
        /*
         * SYSTEM rather than BROADCAST: nobody types a report. It is raised by the
         * worker on a schedule somebody configured once, which is closer to an
         * event-driven notice than to an announcement — and BROADCAST carries the D31
         * constraints, which are about free text reaching everybody.
         */
        origin: 'SYSTEM',
        /*
         * DEFAULT_ON, and never MANDATORY.
         *
         * Subscription IS the consent (design D20): an administrator named this person
         * as a recipient, so nothing further needs to force it through. Making it
         * mandatory would let a tenant push an un-opt-out-able recurring message, which
         * is what D31 refuses for announcements and refuses here for the same reason.
         */
        defaultDisposition: 'DEFAULT_ON',
        tenantMandatable: false,
        platformOnly: false,
        floor: 'LOW',
    },
    VEHICLE_SECURITY: {
        label: 'Vehicle security',
        description: 'Suspected theft, unauthorised movement, tamper indications.',
        origin: 'SYSTEM',
        defaultDisposition: 'DEFAULT_ON',
        // The worked example from the review: a tenant may reasonably insist every
        // member hears about a stolen vehicle.
        tenantMandatable: true,
        platformOnly: false,
        floor: 'LOW',
    },
    OPERATIONAL_INCIDENT: {
        label: 'Operational incidents',
        description: 'Harsh driving, over-speed, geofence exit and related incidents.',
        origin: 'SYSTEM',
        defaultDisposition: 'DEFAULT_ON',
        tenantMandatable: true,
        platformOnly: false,
        floor: 'MEDIUM',
    },
    TELEMETRY_FAULT: {
        label: 'Vehicle faults',
        description: 'Diagnostic trouble codes and engine condition warnings.',
        origin: 'SYSTEM',
        defaultDisposition: 'DEFAULT_ON',
        tenantMandatable: true,
        platformOnly: false,
        floor: 'MEDIUM',
    },
    MAINTENANCE_RECOMMENDATION: {
        label: 'Recommendations',
        description: 'Suggested maintenance and efficiency actions.',
        origin: 'SYSTEM',
        // Useful but never urgent, so it waits to be asked for.
        defaultDisposition: 'OPT_IN',
        tenantMandatable: true,
        platformOnly: false,
        floor: 'LOW',
    },
};
/** Categories a tenant administrator may broadcast in. Derived, not declared twice. */
export const TENANT_BROADCAST_CATEGORIES = ALERT_CATEGORY_KEYS.filter((k) => ALERT_CATEGORIES[k].origin === 'BROADCAST' && !ALERT_CATEGORIES[k].platformOnly);
// ── delivery vocabulary ─────────────────────────────────────────────────────
export const deliveryModeSchema = z.enum(['IMMEDIATE', 'PERIODIC_DIGEST', 'DAILY_SUMMARY']);
/**
 * What the resolver concluded.
 *
 * `UNDELIVERABLE_MANDATORY` is the one that matters (D2): a mandatory category
 * can clamp to no permitted, verified channel — the platform disabled the
 * tenant's only mandated channel, or the person has no verified identity at all.
 * Recording that as a success with zero channels would be a silent hole, so it
 * is an outcome with a name.
 */
export const deliveryDecisionSchema = z.enum([
    'DELIVER',
    'DIGEST',
    'DEFER',
    'SUPPRESS',
    'UNDELIVERABLE_MANDATORY',
]);
/**
 * Why the resolver decided what it did — part of the CONTRACT, not a log line.
 *
 * Without this, "notifications are not arriving" cannot be answered: neither the
 * person nor either administrator can tell which of four policy levels blocked
 * delivery, and that support conversation is unbounded. Same argument ADR 0010
 * §7 made for keeping `FEATURE_NOT_ENABLED` distinct from `FORBIDDEN`.
 *
 * Stable: these strings appear in stored `AlertDelivery` rows, so a value is
 * never repurposed. Add rather than rename.
 */
export const reasonCodeSchema = z.enum([
    // permitted
    'PERMITTED',
    'MANDATORY_OVERRIDE',
    // platform lattice
    'PLATFORM_CHANNEL_DISABLED',
    'PLATFORM_CATEGORY_DISABLED',
    'CHANNEL_NOT_IMPLEMENTED',
    // entitlement
    'PLAN_CHANNEL_NOT_INCLUDED',
    'ALERTING_NOT_ENABLED',
    // tenant lattice
    'TENANT_CHANNEL_DISABLED',
    'TENANT_CATEGORY_UNAVAILABLE',
    'TENANT_SEVERITY_FLOOR',
    'TENANT_QUIET_HOURS',
    // scope lattice — reserved for the deferred role/team/depot layer (§2)
    'SCOPE_CHANNEL_DISABLED',
    'SCOPE_SEVERITY_FLOOR',
    // person
    'USER_CHANNEL_NOT_SELECTED',
    'USER_CATEGORY_DISABLED',
    'USER_CATEGORY_NOT_OPTED_IN',
    'USER_SEVERITY_FLOOR',
    'USER_MUTED',
    'USER_QUIET_HOURS',
    // identity
    'NO_IDENTITY_FOR_CHANNEL',
    'IDENTITY_UNVERIFIED',
    'IDENTITY_SUPPRESSED',
    // volume
    'CATEGORY_FLOOR',
    'VOLUME_CAP_REACHED',
    'RATE_LIMITED_TO_DIGEST',
]);
/** Which policy level decided, for the diagnostic view (D23). */
export const policyScopeSchema = z.enum([
    'PLATFORM',
    'ENTITLEMENT',
    'TENANT',
    'ROLE',
    'TEAM',
    'DEPOT',
    'VEHICLE_GROUP',
    'MEMBERSHIP',
    'IDENTITY',
    'CATEGORY',
]);
/**
 * Precedence order, lowest number first. Platform cannot be overridden; a person
 * decides last among the permission lattice.
 *
 * Declared as data so the resolver walks it generically — that is what makes the
 * deferred role/team/depot layer (§2) new rows rather than a resolver rewrite.
 */
export const SCOPE_PRECEDENCE = {
    PLATFORM: 0,
    ENTITLEMENT: 1,
    TENANT: 2,
    ROLE: 3,
    TEAM: 4,
    DEPOT: 5,
    VEHICLE_GROUP: 6,
    MEMBERSHIP: 7,
    IDENTITY: 8,
    CATEGORY: 9,
};
// ── quiet hours ─────────────────────────────────────────────────────────────
/** `HH:MM`, 24-hour, interpreted in the owner's time zone (D28). */
export const localTimeSchema = z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Expected HH:MM in 24-hour form');
export const quietHoursSchema = z
    .object({
    enabled: z.boolean(),
    /** Windows may wrap midnight — 22:00 to 07:00 is the common case. */
    start: localTimeSchema,
    end: localTimeSchema,
    /** 0 = Sunday, matching `Date.getUTCDay`. Empty means every day. */
    days: z.array(z.number().int().min(0).max(6)).max(7).default([]),
})
    .strict();
// ── policies and preferences ────────────────────────────────────────────────
/**
 * One rule in a policy or preference (§6).
 *
 * A preference is a SET of these rather than a channel flag, because "which
 * channels" answers only one question. Enterprise buyers want conditions: SMS
 * only for critical, email for everything, digest for informational, nothing at
 * weekends.
 */
export const notificationRuleSchema = z
    .object({
    categories: z.array(alertCategorySchema).min(1),
    channels: z.array(channelKeySchema).min(1),
    /** Nothing below this severity matches the rule. */
    minimumSeverity: riskLevelSchema.default('LOW'),
    mode: deliveryModeSchema.default('IMMEDIATE'),
    /** Restrict to a window; omit for any time. */
    window: quietHoursSchema.optional(),
})
    .strict();
/** Platform: the outer bound of the permission lattice (§3). */
export const platformChannelPolicySchema = z
    .object({
    channel: channelKeySchema,
    enabled: z.boolean(),
    /** Operational kill switch, distinct from disablement (D20). */
    suspended: z.boolean().default(false),
    suspendedReason: z.string().max(300).nullable().default(null),
    maxRetries: z.number().int().min(0).max(10).default(3),
    timeoutMs: z.number().int().min(1_000).max(120_000).default(15_000),
})
    .strict();
export const tenantCategoryPolicySchema = z
    .object({
    category: alertCategorySchema,
    disposition: dispositionSchema,
    minimumSeverity: riskLevelSchema.default('LOW'),
    channels: z.array(channelKeySchema),
    /** Whether this category ignores tenant quiet hours. */
    bypassQuietHours: z.boolean().default(false),
})
    .strict();
export const membershipPreferenceSchema = z
    .object({
    rules: z.array(notificationRuleSchema).max(50).default([]),
    quietHours: quietHoursSchema.optional(),
    /** Temporary snooze; null means not muted (§17). */
    mutedUntil: z.string().datetime().nullable().default(null),
    minimumSeverity: riskLevelSchema.default('LOW'),
    /** Categories explicitly turned off, for DEFAULT_ON ones. */
    disabledCategories: z.array(alertCategorySchema).default([]),
    /** Categories explicitly turned on, needed for OPT_IN ones. */
    enabledCategories: z.array(alertCategorySchema).default([]),
})
    .strict();
// ── identity ────────────────────────────────────────────────────────────────
/**
 * `SUPPRESSED` is never cleared automatically (D21) — it must stay recognisable
 * as suppressed, exactly as a revoked refresh token must.
 */
export const identityStatusSchema = z.enum(['PENDING', 'VERIFIED', 'SUPPRESSED']);
export const channelIdentitySchema = z.object({
    id: z.string().uuid(),
    channel: channelKeySchema,
    /** Masked for display — never the full address once stored. */
    displayAddress: z.string(),
    status: identityStatusSchema,
    suppressedReason: z.string().nullable(),
    verifiedAt: z.string().datetime().nullable(),
});
// ── the feature key ─────────────────────────────────────────────────────────
/**
 * Alerting is gated as a whole (ADR 0010), and metered channels are additionally
 * plan-gated because they cost money per message (§11).
 */
export const ALERTING_FEATURE = 'ALERTING';
/** How many days before a cap is reached that the warning fires (D26). */
export const VOLUME_CAP_WARNING_FRACTION = 0.8;
// ── API: what a person sees and changes ─────────────────────────────────────
/**
 * A category as it appears to one person, after policy is applied.
 *
 * `disposition` is the EFFECTIVE one — a category the tenant made mandatory
 * arrives here as MANDATORY, and the screen shows it locked with a reason rather
 * than offering a switch that would be refused. Showing a control that cannot
 * work is worse than showing none.
 */
export const myCategorySchema = z.object({
    category: alertCategorySchema,
    label: z.string(),
    description: z.string(),
    disposition: dispositionSchema,
    /** Channels the tenant permits here. Empty means no tenant restriction. */
    permittedChannels: z.array(channelKeySchema),
    minimumSeverity: riskLevelSchema,
    /** True when the person currently receives it. */
    enabled: z.boolean(),
    /** Channels the person selected. */
    channels: z.array(channelKeySchema),
});
export const myChannelSchema = z.object({
    channel: channelKeySchema,
    label: z.string(),
    description: z.string(),
    /** False when the platform disabled it, the plan excludes it, or it is PLANNED. */
    available: z.boolean(),
    /** Why it is unavailable, for the screen to explain rather than hide. */
    unavailableReason: z.string().nullable(),
    requiresVerification: z.boolean(),
    costClass: z.enum(['FREE', 'METERED']),
    identity: channelIdentitySchema.nullable(),
});
export const myNotificationSettingsSchema = z.object({
    channels: z.array(myChannelSchema),
    categories: z.array(myCategorySchema),
    quietHours: quietHoursSchema,
    mutedUntil: z.string().datetime().nullable(),
    minimumSeverity: riskLevelSchema,
    mode: deliveryModeSchema,
    /** The person's zone, or null to follow the tenant's (D28). */
    timeZone: z.string().nullable(),
    tenantTimeZone: z.string(),
});
export const updateMyNotificationsSchema = z
    .object({
    /** Per-category channel selection. Absent categories are left alone. */
    categories: z
        .array(z.object({
        category: alertCategorySchema,
        enabled: z.boolean(),
        channels: z.array(channelKeySchema),
    }))
        .max(50)
        .optional(),
    quietHours: quietHoursSchema.optional(),
    minimumSeverity: riskLevelSchema.optional(),
    mode: deliveryModeSchema.optional(),
    /** ISO instant, or null to clear the snooze. */
    mutedUntil: z.string().datetime().nullable().optional(),
    timeZone: z.string().max(64).nullable().optional(),
})
    .strict();
export const beginVerificationSchema = z
    .object({
    channel: channelKeySchema,
    /** A mailbox, an E.164 number, or a Telegram chat id. */
    address: z.string().trim().min(3).max(320),
})
    .strict();
/**
 * What happened to the code, not just that a challenge exists.
 *
 * `delivery` is separate from a boolean because there are three outcomes worth
 * telling apart, and the UI must not say "check your inbox" for two of them:
 *
 * - `SENT` — a real transport accepted it.
 * - `NOT_CONFIGURED` — this deployment has no mail server or bot token, so nothing
 *   left the building. The challenge is still valid; it just cannot arrive.
 * - `FAILED` — a configured transport refused it. `error` carries the provider's
 *   own words, which is what makes it fixable.
 *
 * The code itself is never in this response. Returning it would prove only that
 * the caller can read their own API reply, which is not what verification tests.
 */
export const beginVerificationResponseSchema = z.object({
    expiresInMinutes: z.number().int().positive(),
    delivery: z.enum(['SENT', 'NOT_CONFIGURED', 'FAILED']),
    /** Where it was sent from, so a tenant can tell their own server was used. */
    via: z.enum(['TENANT', 'PLATFORM', 'NONE']),
    error: z.string().nullable(),
});
export const confirmVerificationSchema = z
    .object({ channel: channelKeySchema, code: z.string().trim().length(6) })
    .strict();
// ── API: tenant administration ──────────────────────────────────────────────
export const tenantNotificationPolicySchema = z.object({
    timeZone: z.string(),
    quietHours: quietHoursSchema,
    monthlyCap: z.number().int().nullable(),
    categories: z.array(z.object({
        category: alertCategorySchema,
        label: z.string(),
        /** What the registry says before tenant policy. */
        defaultDisposition: dispositionSchema,
        disposition: dispositionSchema,
        minimumSeverity: riskLevelSchema,
        channels: z.array(channelKeySchema),
        bypassQuietHours: z.boolean(),
        mandatory: z.boolean(),
        /** False where the registry forbids a tenant raising it (D31). */
        canMandate: z.boolean(),
        /** True for the platform's own mandatory set — not tenant-editable. */
        platformOwned: z.boolean(),
    })),
});
export const updateTenantNotificationPolicySchema = z
    .object({
    timeZone: z.string().max(64).optional(),
    quietHours: quietHoursSchema.optional(),
    monthlyCap: z.number().int().min(0).nullable().optional(),
})
    .strict();
export const updateAlertCategoryPolicySchema = z
    .object({
    category: alertCategorySchema,
    disposition: dispositionSchema.optional(),
    minimumSeverity: riskLevelSchema.optional(),
    channels: z.array(channelKeySchema).optional(),
    bypassQuietHours: z.boolean().optional(),
    mandatory: z.boolean().optional(),
})
    .strict();
// ── API: the diagnostic (D23) ───────────────────────────────────────────────
/**
 * "Why did this person not get that alert?"
 *
 * The screen that makes the resolver's reason codes worth storing. Delivery
 * history says what happened; this answers for a message that was NEVER SENT,
 * which history structurally cannot.
 */
export const diagnoseRequestSchema = z
    .object({
    userId: z.string().uuid(),
    category: alertCategorySchema,
    channel: channelKeySchema,
    severity: riskLevelSchema.default('HIGH'),
    /** Defaults to now. Supplied so "would it arrive at 3am?" is answerable. */
    at: z.string().datetime().optional(),
})
    .strict();
export const diagnoseStepSchema = z.object({
    scope: policyScopeSchema,
    outcome: z.enum(['ALLOW', 'DENY', 'DEFER', 'OBLIGE']).nullable(),
    reason: reasonCodeSchema.nullable(),
    decided: z.boolean(),
    detail: z.string(),
});
export const diagnoseResponseSchema = z.object({
    decision: deliveryDecisionSchema,
    reason: reasonCodeSchema,
    scope: policyScopeSchema,
    mandatory: z.boolean(),
    /** The precedence chain, with the deciding level marked. */
    trace: z.array(diagnoseStepSchema),
});
// ── API: delivery history ───────────────────────────────────────────────────
export const deliveryHistoryItemSchema = z.object({
    id: z.string().uuid(),
    category: alertCategorySchema.or(z.string()),
    channel: channelKeySchema.or(z.string()),
    status: z.string(),
    decision: deliveryDecisionSchema.or(z.string()),
    reason: reasonCodeSchema.or(z.string()),
    isTest: z.boolean(),
    createdAt: z.string().datetime(),
    sentAt: z.string().datetime().nullable(),
    lastError: z.string().nullable(),
});
export const deliveryHistoryResponseSchema = z.object({
    items: z.array(deliveryHistoryItemSchema),
});
// ── API: platform channel administration ────────────────────────────────────
export const platformChannelSchema = z.object({
    channel: channelKeySchema,
    label: z.string(),
    status: channelStatusSchema,
    enabled: z.boolean(),
    suspended: z.boolean(),
    suspendedReason: z.string().nullable(),
    maxRetries: z.number().int(),
    timeoutMs: z.number().int(),
    monthlyCeiling: z.number().int().nullable(),
    costClass: z.enum(['FREE', 'METERED']),
    /** Non-secret transport settings. Empty until configured. */
    config: z.record(z.unknown()),
    /** A NAME resolved through the secret manager, never the secret. */
    credentialRef: z.string().nullable(),
    /** When it was last proven to work — distinct from merely being configured. */
    verifiedAt: z.string().datetime().nullable(),
    verificationError: z.string().nullable(),
});
export const platformChannelsResponseSchema = z.object({
    channels: z.array(platformChannelSchema),
});
export const updatePlatformChannelSchema = z
    .object({
    channel: channelKeySchema,
    enabled: z.boolean().optional(),
    /** The operational kill switch (D20), distinct from `enabled`. */
    suspended: z.boolean().optional(),
    suspendedReason: z.string().trim().max(300).nullable().optional(),
    maxRetries: z.number().int().min(0).max(10).optional(),
    timeoutMs: z.number().int().min(1000).max(120_000).optional(),
    monthlyCeiling: z.number().int().min(0).nullable().optional(),
})
    .strict()
    .superRefine((v, ctx) => {
    // A suspension with no stated reason is an outage nobody can explain later.
    if (v.suspended === true && !v.suspendedReason) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['suspendedReason'],
            message: 'Give a reason when suspending a channel.',
        });
    }
});
export const suppressionSchema = z.object({
    id: z.string().uuid(),
    userId: z.string().uuid(),
    channel: channelKeySchema.or(z.string()),
    displayAddress: z.string(),
    suppressedReason: z.string().nullable(),
    suppressedAt: z.string().datetime().nullable(),
});
export const suppressionsResponseSchema = z.object({
    items: z.array(suppressionSchema),
});
// ── API: preview, test send and broadcast ───────────────────────────────────
/**
 * Render a template without sending it (D18).
 *
 * Free and unlimited, deliberately: nothing leaves the system, so there is
 * nothing to rate-limit and nothing to charge for. Kept separate from a test send
 * for exactly that reason — conflating them would make the cheap, safe action
 * inherit the expensive one's guards.
 */
export const previewRequestSchema = z
    .object({ templateKey: z.string().min(1).max(64), channel: channelKeySchema })
    .strict();
export const previewResponseSchema = z.object({
    channel: channelKeySchema,
    subject: z.string().nullable(),
    text: z.string(),
    html: z.string().nullable(),
    /** True when the channel's length cap forced a cut. */
    truncated: z.boolean(),
});
/**
 * Send a real test message (D18).
 *
 * To the requester's OWN verified destination only. Firing tests at other members
 * is a way to spam colleagues, and on a metered channel it turns the product into
 * a relay pointed at any address somebody types in. It counts against the tenant
 * volume cap, because exempting it would be a hole in the mechanism whose entire
 * purpose is bounding a bill.
 */
export const testSendSchema = z.object({ channel: channelKeySchema }).strict();
export const testSendResponseSchema = z.object({
    deliveryId: z.string().uuid(),
    displayAddress: z.string(),
});
/** How many messages a broadcast would produce, and where they would stop. */
export const broadcastPreviewRequestSchema = z
    .object({ category: alertCategorySchema, severity: riskLevelSchema.default('HIGH') })
    .strict();
export const broadcastPreviewResponseSchema = z.object({
    /** Active memberships of globally-active users only (D32). */
    recipients: z.number().int(),
    perChannel: z.array(z.object({
        channel: channelKeySchema,
        willSend: z.number().int(),
        willHold: z.number().int(),
        willNotSend: z.number().int(),
    })),
    /**
     * Messages that would be CHARGED for.
     *
     * Reported as a count rather than a currency figure: per-message price varies by
     * provider and destination country, and this system holds no price list.
     * Inventing one would put a number in front of an administrator that looks
     * authoritative and is not.
     */
    meteredMessages: z.number().int(),
    meteredUsedThisMonth: z.number().int(),
    monthlyCap: z.number().int().nullable(),
    /** Required alerts with no permitted, verified channel (D2). */
    undeliverableMandatory: z.number().int(),
    /** Why people would not receive it, so the gaps are visible before sending. */
    blockedBy: z.array(z.object({ reason: reasonCodeSchema, count: z.number().int() })),
});
export const sendBroadcastSchema = z
    .object({
    category: alertCategorySchema,
    title: z.string().trim().min(3).max(120),
    message: z.string().trim().min(3).max(2000),
    severity: riskLevelSchema.default('HIGH'),
})
    .strict();
export const sendBroadcastResponseSchema = z.object({
    eventId: z.string().uuid(),
    /**
     * Resolved at send time and recorded, because membership changes and the list
     * cannot be recomputed afterwards (D32).
     */
    recipients: z.number().int(),
});
/** Broadcasts per administrator per hour. A blunt instrument, and the point. */
export const BROADCAST_RATE_LIMIT_PER_HOUR = 5;
/** Test sends per person per hour. */
export const TEST_SEND_RATE_LIMIT_PER_HOUR = 10;
// ── provider configuration ──────────────────────────────────────────────────
/** Non-secret SMTP settings. The password never travels in this shape. */
export const smtpConfigSchema = z
    .object({
    host: z.string().trim().min(1).max(255),
    port: z.number().int().min(1).max(65535).default(587),
    secure: z.boolean().default(false),
    username: z.string().trim().max(255).nullable().optional(),
})
    .strict();
/**
 * Platform provider configuration.
 *
 * Carries a `credentialRef`, never a secret: whoever configures this also controls
 * the deployment and can put the value in the secret manager. The tenant-level
 * equivalent cannot do that, which is why it looks different (below).
 */
export const updateChannelProviderSchema = z
    .object({
    channel: channelKeySchema,
    config: smtpConfigSchema.optional(),
    credentialRef: z.string().trim().max(120).nullable().optional(),
})
    .strict();
export const verificationResultSchema = z.object({
    ok: z.boolean(),
    /** Null on success. The provider's own words on failure, which is what helps. */
    error: z.string().nullable(),
    verifiedAt: z.string().datetime().nullable(),
});
/**
 * A tenant's own mail server.
 *
 * `password` is WRITE-ONLY — accepted on update, never returned. `hasPassword`
 * says whether one is stored, which is all a form needs to decide between
 * "set a password" and "replace the stored one".
 */
export const tenantSmtpSchema = z.object({
    configured: z.boolean(),
    host: z.string().nullable(),
    port: z.number().int().nullable(),
    secure: z.boolean(),
    username: z.string().nullable(),
    hasPassword: z.boolean(),
    fromEmail: z.string().nullable(),
    fromName: z.string().nullable(),
    enabled: z.boolean(),
    verifiedAt: z.string().datetime().nullable(),
    verificationError: z.string().nullable(),
    /** False when the deployment has no encryption key — the form explains rather than failing on save. */
    storageAvailable: z.boolean(),
});
export const updateTenantSmtpSchema = z
    .object({
    host: z.string().trim().min(1).max(255).optional(),
    port: z.number().int().min(1).max(65535).optional(),
    secure: z.boolean().optional(),
    username: z.string().trim().max(255).nullable().optional(),
    /** Write-only. Null clears it. */
    password: z.string().min(1).max(400).nullable().optional(),
    /**
     * Required once a tenant sends through their own server.
     *
     * Sending via their host while claiming the platform domain would fail THEIR
     * SPF alignment — owning the server is what earns the right to the address.
     */
    fromEmail: z.string().email().max(320).optional(),
    fromName: z.string().trim().max(60).nullable().optional(),
    /**
     * Turning it ON is refused until verification has passed — see the service.
     * A server that has never answered is not a mail path.
     */
    enabled: z.boolean().optional(),
})
    .strict();
// ── credential encryption and key rotation ──────────────────────────────────
/**
 * The state of the one thing OIP encrypts at rest (sender design §10, Q6).
 *
 * Tenant SMTP passwords are sealed in the database because a tenant administrator
 * cannot set an environment variable. Everything the platform configures keeps a
 * `credentialRef` instead, so this screen covers exactly one table.
 *
 * `sealedByVersion` is reported per version rather than as a single "needs
 * rotation" boolean, because during a rotation the interesting question is how many
 * rows are left — a boolean cannot tell a sweep that has not started from one that
 * is nearly done.
 */
export const credentialRotationStatusSchema = z.object({
    /** False when no encryption key is configured; nothing can be sealed at all. */
    configured: z.boolean(),
    /** The version every new seal carries. */
    currentVersion: z.number().int().nullable(),
    /** The retiring key, accepted for reads only. Null outside a rotation window. */
    previousVersion: z.number().int().nullable(),
    /** How many stored credentials sit on each version. */
    sealedByVersion: z.array(z.object({ version: z.number().int(), count: z.number().int() })),
    /** Stored credentials not yet on the current version. */
    outstanding: z.number().int(),
    /**
     * Why a re-seal cannot run right now, or null when it can.
     *
     * A reason rather than a disabled button with no explanation: "no previous key is
     * configured" is a deployment fact the operator has to act on elsewhere.
     */
    blockedReason: z.string().nullable(),
});
export const credentialRotationResultSchema = z.object({
    resealed: z.number().int(),
    /** Already on the current version — a repeat run reports these rather than failing. */
    alreadyCurrent: z.number().int(),
    /**
     * Rows that could not be opened, named by tenant.
     *
     * Reported per tenant and never aborting the sweep: one unreadable credential must
     * not stop the other ninety-nine from being rotated, and the operator needs to
     * know which tenant to go and ask for a new password.
     */
    failures: z.array(z.object({ tenantId: z.string(), tenantName: z.string(), error: z.string() })),
    status: credentialRotationStatusSchema,
});
//# sourceMappingURL=notifications.js.map